import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LuArrowRight, LuCalendar, LuCheck, LuLayoutGrid, LuStar } from 'react-icons/lu';
import { useGoogleLogin } from '@react-oauth/google';
import { useContextHook } from 'use-context-hook';
import GoogleIcon from '../AuthLayout/GoogleIcon';
import SignupFlowShell from '../SignupFlow';
import { PAYWALL_ROUTE, rememberPlan, rememberedPlan, trialEndLabel, useTigalyPlans } from '../SignupFlow/signupPlan';
import { formatEur } from '../Pricing/planFormat';
import { passwordProblem } from '../../../helpers/passwordRules';
import authService from '../../../services/authService';
import { AuthContext } from '../../../Context/authContext';
import { getReferralCode, setReferralCode, clearReferralCode } from '../../../helpers/referralStorage';
import { getCookie, setCookie } from '../../../helpers/common';
import startVerifiedSession from '../../../helpers/startVerifiedSession';
import { hasBlockedPlusAlias, PLUS_ALIAS_MESSAGE } from '../../../helpers/emailRules';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const getReferralCookieDomain = () => {
  const hostname = window?.location?.hostname || '';
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return undefined;
  if (hostname.endsWith('.houdiny.ai') || hostname === 'houdiny.ai') return '.houdiny.ai';
  return undefined;
};

const signupProblem = ({ email, name, password }) => {
  if (!EMAIL_PATTERN.test(email)) return ['email', 'Please enter a valid work email.'];
  if (hasBlockedPlusAlias(email)) return ['email', PLUS_ALIAS_MESSAGE];
  if (name.length < 2) return ['name', 'Please enter your name.'];
  const weakPassword = passwordProblem(password);
  return weakPassword ? ['password', weakPassword] : null;
};

const trackReferral = (email, stripeCustomerId) => {
  if (!window?.promotekit?.refer) return;
  try {
    if (stripeCustomerId) window.promotekit.refer(email, stripeCustomerId);
    else window.promotekit.refer(email);
  } catch (trackErr) {
    console.log('PromoteKit refer tracking failed:', trackErr?.message || trackErr);
  }
};

const SignupReasons = () => {
  const monthly = useTigalyPlans()?.monthly;
  const founding = monthly?.founding !== false;

  return (
    <section className="left" aria-label="Why Tigaly">
      <h2>Your whole outbound stack, 7 days free.</h2>
      <ul className="why">
        <li>
          <span className="ic">
            <LuCalendar />
          </span>
          <div>
            <b>Free until {trialEndLabel()}</b>
            <p>Nothing is charged during the trial. Cancel before it ends and you pay nothing.</p>
          </div>
        </li>
        <li>
          <span className="ic">
            <LuLayoutGrid />
          </span>
          <div>
            <b>Six tools behind one login</b>
            <p>Lead database, LinkedIn + email sequencer, AI writing, Unibox, the AI orchestrator and an MCP server.</p>
          </div>
        </li>
        <li>
          <span className="ic">
            <LuStar />
          </span>
          <div>
            <b>
              {founding
                ? `${formatEur(monthly?.amount ?? 29.99)} a month for the first ${monthly?.seats?.total ?? 150}`
                : `Every tool for ${formatEur(monthly.amount)} a month`}
            </b>
            <p>
              {founding && monthly?.seats ? `${monthly.seats.left} founding seats are left. ` : ''}
              Or pay €399 once with the lifetime deal.
            </p>
          </div>
        </li>
      </ul>
    </section>
  );
};

function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [values, setValues] = useState({ email: '', name: '', password: '' });
  const [problem, setProblem] = useState(null);
  const [loading, setLoading] = useState(false);

  const { onGoogleLogin, loading_user_google, getPermissions } = useContextHook(AuthContext, v => ({
    onGoogleLogin: v.onGoogleLogin,
    loading_user_google: v.loading_user_google,
    getPermissions: v.getPermissions,
  }));

  useEffect(() => {
    rememberPlan(searchParams.get('plan') || rememberedPlan());

    const referralCode = window?.promotekit_referral || searchParams.get('via') || getCookie('referral_code');
    if (!referralCode) return;
    setReferralCode(referralCode);
    setCookie('referral_code', referralCode, 30, getReferralCookieDomain());
  }, [searchParams]);

  const googleLogin = useGoogleLogin({
    onSuccess: ({ access_token }) => onGoogleLogin({ access_token, action: 'Register' }),
  });

  const onChange = e => {
    setValues(current => ({ ...current, [e.target.name]: e.target.value }));
    setProblem(null);
  };

  const register = async ({ email, name, password }) => {
    const resp = await authService.register({
      email,
      password,
      fullName: name,
      referral_code: window?.promotekit_referral || getReferralCode() || getCookie('referral_code'),
    });
    localStorage.setItem('email', email);
    trackReferral(email, resp?.data?.stripeCustomerId);
    clearReferralCode();
    setCookie('referral_code', '', -1, getReferralCookieDomain());
    return resp;
  };

  const onSubmit = async e => {
    e.preventDefault();
    const typed = { email: values.email.trim(), name: values.name.trim(), password: values.password };
    const found = signupProblem(typed);
    if (found) {
      setProblem(found);
      return;
    }

    setLoading(true);
    try {
      const resp = await register(typed);
      // Local development can skip the code: the account comes back signed in.
      if (resp?.data?.session?.token) {
        await startVerifiedSession(resp.data.session);
        await getPermissions();
        navigate(`${PAYWALL_ROUTE}?plan=${rememberedPlan()}`, { replace: true });
        return;
      }
      navigate('/verify-otp', {
        state: { expiresIn: resp?.data?.emailSent !== false ? resp?.data?.otpExpiresInSeconds : undefined },
      });
    } catch (error) {
      setProblem([null, error.message === 'Failed to fetch' ? 'No connection. Please try again.' : error.message]);
      setLoading(false);
    }
  };

  const invalid = field => (problem?.[0] === field ? 'true' : undefined);

  return (
    <SignupFlowShell variant="su">
      <main className="grid">
        <SignupReasons />

        <div className="card">
          <h1>Create your Tigaly account</h1>
          <p className="sub">Start generating leads for free.</p>

          <button type="button" className="google" onClick={googleLogin} disabled={loading_user_google}>
            <GoogleIcon /> Continue with Google
          </button>
          <div className="or">or</div>

          <form onSubmit={onSubmit} noValidate>
            <label className="f">
              <span className="lbl">Work email</span>
              <input
                className="in"
                name="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                maxLength={200}
                placeholder="you@company.com"
                value={values.email}
                onChange={onChange}
                aria-invalid={invalid('email')}
              />
            </label>
            <label className="f">
              <span className="lbl">Full name</span>
              <input
                className="in"
                name="name"
                autoComplete="name"
                maxLength={100}
                placeholder="Your name"
                value={values.name}
                onChange={onChange}
                aria-invalid={invalid('name')}
              />
            </label>
            <label className="f">
              <span className="lbl">Password</span>
              <input
                className="in"
                name="password"
                type="password"
                autoComplete="new-password"
                maxLength={128}
                placeholder="At least 8 characters"
                value={values.password}
                onChange={onChange}
                aria-invalid={invalid('password')}
              />
            </label>
            <p className={`err${problem ? ' show' : ''}`} role="alert">
              {problem?.[1]}
            </p>
            <button className={`btn${loading ? ' busy' : ''}`} type="submit" disabled={loading}>
              <span>Create account</span>
              <i className="spin" />
              <LuArrowRight className="go" />
            </button>
          </form>

          <p className="fine">
            Already have an account? <Link to="/">Sign in</Link>
            <br />
            By continuing you agree to the{' '}
            <a href="/terms.html" target="_blank" rel="noreferrer">
              Terms
            </a>
            .
          </p>
          <div className="trust">
            <span>
              <LuCheck /> 7 days free
            </span>
            <span>
              <LuCheck /> €0 today
            </span>
            <span>
              <LuCheck /> Cancel anytime
            </span>
          </div>
        </div>
      </main>
    </SignupFlowShell>
  );
}

export default Signup;
